import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, Lock, Plus, Search, ShieldAlert, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ESTADOS_FID, SEMAFORO_COBERTURA } from "@/lib/fideicomisos";
import { toast } from "sonner";

function EstadoBadge({ estado }) {
  const m = ESTADOS_FID[estado] || { label: estado, cls: "bg-slate-100 text-slate-700" };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

export default function Fideicomisos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const esAdmin = user?.role === "admin_entidad";
  const [eleg, setEleg] = useState(null);
  const [rows, setRows] = useState([]);
  const [estado, setEstado] = useState("todos");
  const [q, setQ] = useState("");
  const [nuevo, setNuevo] = useState(false);
  const [form, setForm] = useState({ denominacion: "", notas_internas: "" });
  const [busy, setBusy] = useState(false);
  const [errorAcceso, setErrorAcceso] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/fideicomisos/elegibilidad");
      setEleg(data);
      if (data.estado_modulo === "activo") {
        const params = {};
        if (estado !== "todos") params.estado = estado;
        if (q.trim()) params.q = q.trim();
        const r = await api.get("/fideicomisos", { params });
        setRows(r.data);
      }
    } catch (e) {
      setErrorAcceso(apiError(e));
    }
  }, [estado, q]);

  useEffect(() => { load(); }, [load]);

  const crear = async () => {
    if (!form.denominacion.trim()) { toast.error("Indica la denominación del fideicomiso"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/fideicomisos", {
        denominacion: form.denominacion.trim(),
        tipo_modelo: "creditos_propios",
        notas_internas: form.notas_internas,
      });
      toast.success(`Fideicomiso ${data.folio} creado (estado: prospecto).`);
      setNuevo(false);
      setForm({ denominacion: "", notas_internas: "" });
      navigate(`/fideicomisos/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (errorAcceso)
    return (
      <div>
        <PageHeader title="Fideicomisos de garantía" subtitle="LGTOC Arts. 395–407" />
        <div className="flex flex-col items-center gap-3 p-16 text-slate-400" data-testid="fid-sin-acceso">
          <ShieldAlert className="h-10 w-10" />
          <p className="max-w-md text-center text-sm">{errorAcceso}</p>
        </div>
      </div>
    );

  if (!eleg) return <div className="p-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  if (eleg.estado_modulo !== "activo")
    return (
      <div>
        <PageHeader title="Fideicomisos de garantía" subtitle="LGTOC Arts. 395–407 — la SOFOM como fiduciaria de sus propios créditos" />
        <div className="p-6 md:p-8">
          <div className="mx-auto max-w-xl rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="fid-modulo-bloqueado">
            <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <Lock className="h-6 w-6 text-amber-600" />
            </span>
            <h2 className="font-heading text-base font-semibold text-slate-900">
              {eleg.estado_modulo === "suspendido" ? "Módulo suspendido por la plataforma" : "Módulo pendiente de activación"}
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              {eleg.estado_modulo === "suspendido"
                ? `La plataforma suspendió el módulo para tu entidad.${eleg.suspension?.motivo ? ` Motivo: ${eleg.suspension.motivo}` : ""} Contacta a Felvora para reactivarlo.`
                : "Para operar fideicomisos de garantía, el administrador debe acreditar la elegibilidad: registro CONDUSEF vigente (evidencia PDF) y aceptación de la declaración del Art. 87-Ñ LGOAAC."}
            </p>
            {esAdmin && eleg.estado_modulo !== "suspendido" && (
              <Button data-testid="fid-ir-configuracion" className="mt-5" onClick={() => navigate("/fideicomisos/configuracion")}>
                Completar elegibilidad
              </Button>
            )}
          </div>
        </div>
      </div>
    );

  const porEstado = rows.reduce((a, r) => { a[r.estado] = (a[r.estado] || 0) + 1; return a; }, {});

  return (
    <div>
      <PageHeader title="Fideicomisos de garantía" subtitle="Cartera fiduciaria — LGTOC Arts. 395–407 (créditos propios)" />
      <div className="space-y-6 p-6 md:p-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <StatCard label="Fideicomisos totales" value={rows.length} icon="Landmark" accent="primary" />
          <StatCard label="Vigentes" value={porEstado.vigente || 0} icon="CheckCircle2" accent="green" />
          <StatCard label="En integración" value={(porEstado.prospecto || 0) + (porEstado.en_integracion || 0)} icon="Hammer" accent="amber" />
          <StatCard label="En ejecución" value={porEstado.en_ejecucion || 0} icon="Gavel" accent="primary" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="fid-buscar" className="w-64 pl-8" placeholder="Folio o denominación…"
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger data-testid="fid-filtro-estado" className="w-48">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los estados</SelectItem>
              {Object.entries(ESTADOS_FID).map(([v, m]) => (
                <SelectItem key={v} value={v}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex-1" />
          {esAdmin && (
            <>
              <Button data-testid="fid-btn-configuracion" variant="outline" onClick={() => navigate("/fideicomisos/configuracion")}>
                Configuración
              </Button>
              <Button data-testid="fid-btn-nuevo" onClick={() => setNuevo(true)}>
                <Plus className="mr-1.5 h-4 w-4" /> Nuevo fideicomiso
              </Button>
            </>
          )}
        </div>

        <DataTable testid="table-fideicomisos" rows={rows}
          onRowClick={(r) => navigate(`/fideicomisos/${r.id}`)}
          columns={[
            { key: "folio", label: "Folio" },
            { key: "denominacion", label: "Denominación" },
            { key: "tipo_modelo", label: "Modelo", render: () => "Créditos propios" },
            { key: "estado", label: "Estado", render: (r) => <EstadoBadge estado={r.estado} /> },
            { key: "cobertura_semaforo", label: "Cobertura", render: (r) => {
                if (!r.cobertura_semaforo) return <span className="text-xs text-slate-400">N/A</span>;
                const m = SEMAFORO_COBERTURA[r.cobertura_semaforo] || {};
                return (
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`} data-testid={`fid-cobertura-${r.id}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
                    {r.cobertura != null ? `${Number(r.cobertura).toFixed(2)}x` : ""}
                    {r.avaluos_vencidos > 0 ? " · avalúo vencido" : ""}
                  </span>
                );
              } },
            { key: "partes_activas", label: "Partes activas" },
            { key: "fecha_constitucion", label: "Constitución", render: (r) => r.fecha_constitucion || "—" },
          ]}
          emptyLabel="Sin fideicomisos registrados. Crea el primero con 'Nuevo fideicomiso'." />
      </div>

      <Dialog open={nuevo} onOpenChange={setNuevo}>
        <DialogContent className="sm:max-w-md" data-testid="fid-dialog-nuevo">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Landmark className="h-5 w-5" /> Nuevo fideicomiso de garantía
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Denominación *</label>
              <Input data-testid="fid-nuevo-denominacion" placeholder="Ej. Fideicomiso de garantía crédito refaccionario Pérez"
                value={form.denominacion} onChange={(e) => setForm((f) => ({ ...f, denominacion: e.target.value }))} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Modelo</label>
              <Input value="Créditos propios (la SOFOM como fiduciaria)" disabled />
              <p className="mt-1 text-[11px] text-slate-400">El modelo "créditos de terceros" no está habilitado en esta fase.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Notas internas</label>
              <Textarea data-testid="fid-nuevo-notas" rows={3} value={form.notas_internas}
                onChange={(e) => setForm((f) => ({ ...f, notas_internas: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuevo(false)}>Cancelar</Button>
            <Button data-testid="fid-nuevo-crear" onClick={crear} disabled={busy || !form.denominacion.trim()}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Crear fideicomiso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
